
import {wamp_last_error_code, wamp_init, wamp_dispose, wamp_isInit, wamp_call, wamp_subscribe, wamp_unsubscribe} from "../wamp_client";
class DeviceWebRtcCall {
  callOptions;
  callId;
  subscriptionId;
  pc;
  audioTrack;
  incommingStream;

  previousView; // The child element of viewId before starting the call so that it can be restored later

  lastErrorCode;
  lastErrorString;

  callEventCallback;
  connectionStateCallback;

  heartbeatTimeoutSecs;
  heartbeatTimeout;

  callStartedTimestamp;
  playerStateChangedTimestamp;
  playerStatePlayingTimestamp;

  constructor(viewId, deviceId, deviceServiceId, callEventCallback, connectionStateCallback, iceServers, iceTransportPolicy) {
    this.viewId = viewId;
    // El deviceServiceId puede ser un MediaInut, un MediaStream, un MediaOutput, etc. En función de las opciones
    // especificadas en el método startCall se seleccionará un(os) sub-servicio(s), o se usará el especificado directamente.
    this.deviceId = deviceId;
    this.deviceServiceId = deviceServiceId;

    this.callEventCallback = callEventCallback;
    this.connectionStateCallback = connectionStateCallback;

    this.iceServers = iceServers;
    this.iceTransportPolicy = iceTransportPolicy;

    this.eventProcessorCallback = (data) => this.eventProcessor(data);
    this.heartbeatCallback = () => this.heartbeat();
  }

  setError(errorCode, errorString) {
    this.lastErrorCode = errorCode;
    this.lastErrorString = errorString;
    console.error(errorCode + ' - ' + errorString);
  }
 
  async startCall(options) {
    let ret = false;
    // Inicializa la llamada contra el deviceService usando una llamada wamp.
    // Una vez recibe el id de la llamada, se suscribe para detectar los cambios.
    // Cuando la llamada se haya inciado, se inserta el player de vídeo en el view
    // Las options están documentadas en el RFC del servidor de señalización

    this.receivedIceServers = null;
    this.callStartedTimestamp = Date.now();
    try {
      let res = await wamp_call('allocateCall', {
        deviceId: this.deviceId, 
        deviceServiceId: this.deviceServiceId, 
        options: options
      });

      if (res.argsDict.status) {
        let call_uid = res.argsDict.call_uid;
        this.callId = res.argsDict.call_id.toString();
        this.callOptions = options;
        let iceServers = res.argsDict.ice_servers;
        if (iceServers && iceServers.length > 0) {
          try {
            this.receivedIceServers = JSON.parse(iceServers);
          } catch (e) {
            console.error('Invalid ICE Servers', iceServers);
            console.error(e);
          }
        }
        console.log('ICE Servers are:', res.argsDict.ice_servers);
        this.subscriptionId = await wamp_subscribe(call_uid, this.eventProcessorCallback);
        if (this.subscriptionId >= 0) {
          let res = await wamp_call('startCall', {
            callId: this.callId
          }, 30*1000);
          
          if (res.argsDict.status) {
            ret = true;

            if (res.argsDict.heartbeatSecs) {
              this.heartbeatTimeoutSecs = res.argsDict.heartbeatSecs;
              this.heartbeat();
            }

          } else {
            console.error('Failed to start allocated call');
          }
        } else {
          console.error('Error subscribing to ' + call_uid +' ' + wamp_last_error_code());
        }
      } else {
        this.setError(res.argsDict.errorCode ?? 'InvalidResponse', 'Invalid server response: ' + res.argsDict.errorDesc);
        ret = false;
      }
        
    } catch (e) {
      console.error('RPC call failed', e);
      this.setError('Connection', 'Invalid server connection');
    }

    return ret;
  }
 
  async endCall(shouldHangup=true) {
    let ret = false;

    if (this.ended) {
      console.warn('Call already ended');
      return;
    }

    this.ended = true;
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }

    const view = document.getElementById(this.viewId);
    if (view && view.firstChild) {
      if (view.firstChild.nodeName == 'VIDEO') {
        // Remove the stream
        const video = view.firstChild;
        video.srcObject = null;
      }

      if (this.previousView) {
        view.firstChild.replaceWith(this.previousView);
      } else {
        view.firstChild.remove();
      }
      
    } else if (view && this.previousView) {
      view.appendChild(this.previousView);
    }

    if (shouldHangup) {
      let stats = await this.getStats();
      try {
        let res = await wamp_call('hangUp', {callId: this.callId, stats: stats});
        if (res.argsDict.status) {
          wamp_unsubscribe(this.subscriptionId, this.eventProcessorCallback);
          ret = true;
        } else {
          console.error('Hangup failed');
          this.setError(res.argsDict.errorCode ?? 'InvalidResponse', 'Invalid server response: ' + res.argsDict.errorDesc);
        }
        
      } catch (e) {
        console.error('Hangup RPC call failed', e);
        this.setError('Connection', 'Invalid server connection');
      }
    }

    this.callId = null;
    this.subscriptionId = null;
  
    // Make sure the handle offer promise is finished
    if (this.handleOfferPromise) {
      await this.handleOfferPromise;
    }

    try {
      if (this.pc && this.pc.getTransceivers()) {
        this.pc.getTransceivers().forEach((transceiver) => {
          if (transceiver.stop) {
            transceiver.stop();
          }
        });
      }
    
      // close local audio/video
      if (this.pc && this.pc.getSenders()) {
        this.pc.getSenders().forEach((sender) => {
          const track = sender.track;
          if (track !== null) {
            sender.track.stop();
          }
        });
      }
    } catch (e) {
      console.error('Error stopping WebRTC components', e);
    }

    // close peer connection
    setTimeout(() => {
      console.log('Clearing peer connection');
      if (this.pc) {
        this.pc.close();
      }
      
      this.pc = null;
    }, 100);

    return ret;
  }
 
  async heartbeat() {
    let ok = false;

    if (this.ended) {
      return true;
    }

    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }

    let stats = await this.getStats();
    try {
      let res = await wamp_call('callHeartbeat', {callId: this.callId, stats: stats});
      if (res.argsDict.status) {
        ok = true;
      } else {
        this.setError(res.argsDict.errorCode ?? 'InvalidResponse', 'Invalid server response: ' + res.argsDict.errorDesc);
      }
      
    } catch (e) {
      console.error('RPC call failed', e);
      this.setError('Connection', 'Invalid server connection');
    }

    if (ok) {
      if (!this.ended) {
        this.heartbeatTimeout = setTimeout(this.heartbeatCallback, this.heartbeatTimeoutSecs*1000);
      }
    } else {
      console.error('Heartbeat failed. Ending call');
      this.endCall();
    }
    
  }

  async setEnabledStream(streamId, enabled) {
    let ret = false;
    if (streamId != 'audio-in') {
      console.log('Invalid stream id', streamId);
      this.setError('BadCall', 'Invalid stream');
      return ret;
    }

    if (!this.audioTrack){
      console.error('Stream not available', streamId);
      this.setError('BadCall', 'Stream not available');
      return ret;
    }
    
    try {
      this.audioTrack.enabled = enabled;
      let res = await wamp_call('setEnabledStream', {
        callId: this.callId, 
        streamId: streamId,
        enabled: enabled
      });
      console.log('RPC successfully called. Response is ' + res);
      console.log(res);
  
      if (res.argsDict.status) {
        ret = true;
      } else {
        console.log('Invalid response');
        this.setError(res.argsDict.errorCode ?? 'InvalidResponse', 'Invalid server response: ' + res.argsDict.errorDesc);
      }
      
    } catch (e) {
      console.error('RPC call failed', e);
      this.setError('Connection', 'Invalid server connection');
    }

    return ret;
  }

  isEnabledStream(streamId) {
    let ret = false;
    if (streamId != 'audio-in') {
      console.log('Invalid stream id', streamId);
      this.setError('BadCall', 'Invalid stream');
      return ret;
    }

    ret = this.audioTrack && this.audioTrack.enabled;

    return ret;
  }

  async setPaused(pause) {
    let ret = false;

    try {
      let res = await wamp_call('setPausedCall', {
        callId: this.callId,
        paused: pause
      }, 60*1000);
      console.log('RPC successfully called. Response is ' + res);
      console.log(res);
  
      if (res.argsDict.status) {
        ret = true;
      } else {
        console.log('Invalid response');
        this.setError(res.argsDict.errorCode ?? 'InvalidResponse', 'Invalid server response: ' + res.argsDict.errorDesc);
      }
      
    } catch (e) {
      console.error('RPC call failed', e);
      this.setError('Connection', 'Invalid server connection');
    }

    return ret;
  }

  async isPaused() {
    let ret = false;

    try {
      let res = await wamp_call('isPausedCall', {
        callId: this.callId,
      });
      console.log('RPC successfully called. Response is ' + res);
      console.log(res);
  
      if (res.argsDict.status) {
        ret = res.argsDict.paused;
      } else {
        console.log('Invalid response');
        this.setError(res.argsDict.errorCode ?? 'InvalidResponse', 'Invalid server response: ' + res.argsDict.errorDesc);
      }
      
    } catch (e) {
      console.error('RPC call failed', e);
      this.setError('Connection', 'Invalid server connection');
    }

    return ret;
  }
 
  lastError() {
    // Devuelve un objeto con un errorCode y un errorString asociados al último error ocurrido
    return {
      lastErrorCode: lastErrorCode,
      lastErrorString: lastErrorString
    };
  }

  async setMutedAudio(muted) {
    let ret = false;
    // Get the video
    const view = document.getElementById(this.viewId);

    if (view) {
      let video = view.firstChild;
      if (video && video.nodeName == 'VIDEO') {
        video.muted = muted;
        ret = true;
      } else if (this.callOptions) {
        // Check the property that the player is going to use when created
        this.callOptions.audio_muted = muted;
        ret = true;
      } else {
        ret = false;
      }
    } else {
      console.error('No view');
      this.setError('BadCall', 'Invalid view');
    }

    return ret;
  }

  isMutedAudio() {
    let ret = false;

    // Get the video
    const view = document.getElementById(this.viewId);

    if (view) {
      let video = view.firstChild;
      if (video && video.nodeName == 'VIDEO') {
        ret = video.muted;
      } else if (this.callOptions) {
        // Check the property that the player is going to use when created
        ret = this.callOptions.audio_muted;
      } else {
        ret = false;
      }
    } else {
      console.error('No view');
      this.setError('BadCall', 'Invalid view');
    }

    return ret;
  }

  async eventProcessor(eventData) {
    if (this.ended) {
      return;
    }

    var event = JSON.parse(eventData.argsList[0]);

    const code = event.properties.code;
    if (code == 'CallSdpOfferChanged') {
      const message = JSON.parse(event.properties.sdpOffer);
      this.handleOfferPromise = this.handleOffer(event.entityId.toString(), message);
      await this.handleOfferPromise;
    }

    if (this.callEventCallback) {
      this.callEventCallback(event);
    }
  }
 
  async handleOffer(callId, offer) {
    if (this.ended) {
      return;
    }

    this.pc = this.createPeerConnection();
    await this.pc.setRemoteDescription(offer);
    
    if (this.ended) {
      return;
    }

    if (this.callOptions.audio == 'send' || this.callOptions.audio == 'sendrecv'){
      try {
        const media = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        let tracks = media.getTracks();
        this.audioTrack = tracks[0];
        if (!this.callOptions.audio_enabled || !this.callOptions.audio_enabled.includes('send')) {
          this.audioTrack.enabled = false;
        }

        tracks.forEach(track => this.pc.addTrack(track, media));
      } catch (e) {
        console.error('Could not get media tracks');
        console.error(e);
      }
    }
  
  
    await this.pc.setLocalDescription(await this.pc.createAnswer());
    await this.waitGatheringComplete();

    // Do not send the answer if it's not needed
    if (this.ended) {
      return;
    }
  
    const answer = this.pc.localDescription;
    try {
      let res = await wamp_call('setSdpAnswer', {callId: callId, answer: JSON.stringify({type: answer.type, sdp: answer.sdp})});
  
      if (res.argsDict.status) {
        console.log('setSdpAnswer successfully called', res);
      } else {
        console.log('Invalid response', res);
      }
      
    } catch (e) {
      console.error('RPC call failed', e);
    }

    this.handleOfferPromise = null;
  }
 
  createPeerConnection() {
    if (this.ended) {
      return;
    }
    
    const config = {
      bundlePolicy: "max-bundle",
    };
  
    if (this.receivedIceServers && this.receivedIceServers.iceServers) {
      config.iceServers = this.receivedIceServers.iceServers;
    } else if (this.usedIceServers) {
      config.iceServers = this.iceServers;
    }

    if (this.receivedIceServers && this.receivedIceServers.iceTransportPolicy) {
      config.iceTransportPolicy = this.receivedIceServers.iceTransportPolicy;
    } else if (this.iceTransportPolicy) {
      config.iceTransportPolicy = this.iceTransportPolicy;
    }
  
    let pc = new RTCPeerConnection(config);
  
    // Register some listeners to help debugging
    /*pc.addEventListener('iceconnectionstatechange', () =>
      iceConnectionLog.textContent += ' -> ' + pc.iceConnectionState);
    iceConnectionLog.textContent = pc.iceConnectionState;
  
    pc.addEventListener('icegatheringstatechange', () =>
      iceGatheringLog.textContent += ' -> ' + pc.iceGatheringState);
    iceGatheringLog.textContent = pc.iceGatheringState;
  
    pc.addEventListener('signalingstatechange', () =>
      signalingLog.textContent += ' -> ' + pc.signalingState);
    signalingLog.textContent = pc.signalingState;*/

    pc.addEventListener('connectionstatechange', () => {
      if (this.connectionStateCallback && !this.ended) {
        this.connectionStateCallback(pc.connectionState);
      }
    });
  
    // Receive audio/video track
    pc.ontrack = async (evt) => {
      if (this.ended) {
        return;
      }

      let video;
      const view = document.getElementById(this.viewId);


      if (view.firstChild && view.firstChild.nodeName == 'VIDEO') {
        video = view.firstChild;
      } else {
        video = document.createElement('video');
        video.style.width = '100%';
        video.style.height = '100%';
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        // Set an invisible poster. Otherwise, android shows an ugly play image
        // This is the smallest transparent image we can use
        // https://stackoverflow.com/questions/6018611/smallest-data-uri-image-possible-for-a-transparent-image#13139830
        video.setAttribute('poster', 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7');

        // Used to generate the stats
        video.addEventListener("loadeddata", () => {
          if (!this.playerStateChangedTimestamp) {
            this.playerStateChangedTimestamp = Date.now();
          }
          console.log('Player loaded data', this.playerStateChangedTimestamp);

          // Create a heartbeat event so that stats are updated
          this.heartbeat();
        });

        video.addEventListener("playing", () => {
          if (!this.playerStatePlayingTimestamp) {
            this.playerStatePlayingTimestamp = Date.now();
          }
          console.log('Player playing data', this.playerStatePlayingTimestamp);

          // Create a heartbeat event so that stats are updated
          this.heartbeat();
        });

        if (this.callOptions.audio_muted) {
          video.muted = true;
        } else {
          video.muted = false;
        }

        if (view.firstChild) {
          this.previousView = view.firstChild;
          view.firstChild.replaceWith(video);
        } else {
          view.appendChild(video);
        }
      }

      // This event will be emitted once per track, so if we receive audio and video, it'll
      // be emitted twice, but usually (always in our case) the media stream will be the same.
      // We suppose every track is in the same media stream, so we can perform this once
      // preventing the exception "DOMException: The play() request was interrupted by a new load request"
      if (!video.srcObject) {
        this.incommingStream = evt.streams[0];
        video.srcObject = evt.streams[0]; // The stream groups audio and video tracks
        video.play().catch(error => {
          console.log(error);
        });
      }
    };
  
    return pc;
  }

  async waitGatheringComplete() {
    return new Promise((resolve) => {
      if (this.pc && this.pc.iceGatheringState === 'complete') {
        resolve();
      } else if (this.pc) {
        this.pc.addEventListener('icegatheringstatechange', () => {
          if (this.pc.iceGatheringState === 'complete') {
            resolve();
          }
        });
      }
    });
  }

  async getStats() {
    let videoStats = {};
    let audioStats = {}

    // The metrics' names have to end with
    // - _duration if it's a duration (always in seconds)
    // - _counter if it's a counter (it's always incremented)
    // - _value if it's a discrectional value

    if (!this.playerStateChangedTimestamp) {
      console.log('Player not started', this.viewId);
    } else if (!this.pc) {
      console.log('Peer connection not available', this.viewId);
    } else {
      let rtcStats = await this.pc.getStats();
      rtcStats.forEach((report) => {
        if (report.type === "inbound-rtp" && report.kind === "video") {
          console.log(report);
          videoStats['firCount_counter'] = report.firCount;
          videoStats['freezeCount_counter'] = report.freezeCount;
          videoStats['packetsLost_counter'] = report.packetsLost;
          videoStats['pliCount_counter'] = report.pliCount;
          videoStats['nackCount_counter'] = report.nackCount;
          videoStats['packetsDiscarded_counter'] = report.packetsDiscarded;
          videoStats['jitterBufferEmittedCount_counter'] = report.jitterBufferEmittedCount; // Number of samples out of the jitter buffer

          videoStats['framesReceived_counter'] = report.framesReceived;
          videoStats['framesDecoded_counter'] = report.framesDecoded;
          videoStats['framesDropped_counter'] = report.framesDropped;
          videoStats['framesRendered_counter'] = report.framesRendered;
        } else if (report.type === "inbound-rtp" && report.kind === "audio") {
          console.log(report);
          audioStats['packetsLost_counter'] = report.packetsLost;
          audioStats['nackCount_counter'] = report.nackCount;
          audioStats['packetsDiscarded_counter'] = report.packetsDiscarded;
          audioStats['jitterBufferEmittedCount_counter'] = report.jitterBufferEmittedCount; // Number of samples out of the jitter buffer

          audioStats['totalSamplesReceived_counter'] = report.totalSamplesReceived;
          audioStats['insertedSamplesForDeceleration_counter'] = report.insertedSamplesForDeceleration;
          audioStats['removedSamplesForAcceleration_counter'] = report.removedSamplesForAcceleration;
        }
      });
    }

    return {
      ... (navigator && navigator.userAgent) && {'userAgent': navigator.userAgent},
      ... (this.callStartedTimestamp && this.playerStateChangedTimestamp) && {'timeToFirstFrame_duration': (this.playerStateChangedTimestamp - this. callStartedTimestamp)/1000} ,
      ... (this.callStartedTimestamp && this.playerStatePlayingTimestamp) && {'timeToFirstPlayedFrame_duration': (this.playerStatePlayingTimestamp - this. callStartedTimestamp)/1000} ,
      'video': videoStats,
      'audio': audioStats,
    };
  }
}
export { DeviceWebRtcCall };
